import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { cognitoLogout, logoutCookie } from '@guryou/redux';
import { Auth } from 'aws-amplify';
import {
  clearWidgetAuthIntegrationContext,
  readWidgetAuthBrandingFromSession,
  readWidgetAuthReturnUrlFromSession,
  clearWidgetAuthUserHintFromStorage,
} from 'constants/widgetIntegrationBranding';
import Logo from 'assets/img/logo.png';
import LogoIcon from 'assets/img/guryouIcons/gyIcon37.png';
import { Icon } from '../shared';
import { useFeelPanelBranding } from 'components/FeelPanelBrandingContext';
import useUnreadMessages from '../../hooks/useUnreadMessages';

const menuItems = [
  { label: 'BOOK', route: `/book`, requiresLogin: false, icon: 'book' },
  { label: 'VIDEO', route: `/videos`, requiresLogin: false, icon: 'video' },
];

const Sidebar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const auth = useSelector(s => s.auth);
  const { hasUnreadMessages } = useUnreadMessages();
  const { resolvedPanelBranding } = useFeelPanelBranding();
  const widgetAuthBranding = readWidgetAuthBrandingFromSession();
  const providerLogo = resolvedPanelBranding?.logoUrl || widgetAuthBranding?.logoUrl || null;
  const logoAlt = resolvedPanelBranding?.brandName || widgetAuthBranding?.brandName || 'Guryou';

  const accent = resolvedPanelBranding?.accentColor;
  const authSessionAccent = widgetAuthBranding?.accentColor || null;
  const isProviderRoute = /^\/(en|it)\/provider\/\d+/.test(location.pathname);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', isOpen ? '256px' : '80px');
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (showDropdown && !event.target.closest('[data-user-dropdown]')) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleLogout = async () => {
    const returnUrl =
      readWidgetAuthReturnUrlFromSession() || (document.referrer && !document.referrer.startsWith(window.location.origin) ? document.referrer : null);
    clearWidgetAuthIntegrationContext();
    logoutCookie();
    clearWidgetAuthUserHintFromStorage();
    setShowDropdown(false);
    if (returnUrl) {
      try {
        await Auth.signOut();
      } catch (_) {
        // ignore sign-out failures; user is leaving this app anyway
      }
      dispatch({ type: 'LOGOUT' });
      window.location.replace(returnUrl);
      return;
    }
    await dispatch(cognitoLogout());
  };

  const isActive = route => {
    if (route === '/') {
      return location.pathname === '/';
    }
    if (route === '/videos' && (location.pathname.startsWith('/video-courses/') || location.pathname.startsWith('/videos/'))) {
      return true;
    }
    // Provider details page belongs to booking flow in navigation.
    if (route === '/book' && /^\/(en|it)\/provider\/\d+/.test(location.pathname)) {
      return true;
    }
    return location.pathname.startsWith(route);
  };

  const activeBgColor = route => {
    if (isProviderRoute && route === '/book' && authSessionAccent) {
      return authSessionAccent;
    }
    if (route === '/videos' && location.pathname.startsWith('/video-courses/') && authSessionAccent) {
      return authSessionAccent;
    }
    return accent ?? 'var(--feel-primary)';
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-white shadow-lg z-30 flex flex-col transition-[width] duration-200 ease-out ${
        isOpen ? 'w-64' : 'w-20'
      }`}
    >
      <div className="flex flex-col h-full">
        <div className={`border-b border-gray-200 flex items-center ${isOpen ? 'p-6 justify-between' : 'p-4 justify-center'}`}>
          {isOpen ? (
            <>
              <Link to="/" className="flex items-center min-w-0">
                {providerLogo ? (
                  <img src={providerLogo} key={providerLogo} className="h-8 max-w-[160px] w-auto object-contain" alt={logoAlt} decoding="async" />
                ) : (
                  <img src={Logo} className="h-8" alt={logoAlt} />
                )}
              </Link>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Fechar sidebar"
                title="Fechar menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.99994 10 7 11.9999l1.99994 2M12 5v14M5 4h14c.5523 0 1 .44772 1 1v14c0 .5523-.4477 1-1 1H5c-.55228 0-1-.4477-1-1V5c0-.55228.44772-1 1-1Z"
                  />
                </svg>
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Abrir sidebar"
              title="Abrir menu"
            >
              {providerLogo ? (
                <img src={providerLogo} key={providerLogo} className="h-8 w-8 rounded-full object-cover" alt={logoAlt} decoding="async" />
              ) : (
                <img src={LogoIcon} className="h-8 w-8" alt={logoAlt} />
              )}
            </button>
          )}
        </div>

        <nav className={`flex-1 py-6 space-y-2 overflow-y-auto ${isOpen ? 'px-4' : 'px-2'}`}>
          {auth.data && (
            <button
              onClick={() => history.push('/')}
              className={`flex items-center ${isOpen ? 'space-x-3 px-4' : 'justify-center px-2'} py-3 rounded-lg w-full ${
                isActive('/') && location.pathname === '/' ? 'text-white' : 'text-gray-700 hover:bg-gray-100'
              }`}
              style={isActive('/') && location.pathname === '/' ? { backgroundColor: activeBgColor('/') } : undefined}
              title={!isOpen ? t('GENERAL.home', 'Home') : ''}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                />
              </svg>
              {isOpen && <span className="font-medium text-sm">{t('GENERAL.home', 'Home')}</span>}
            </button>
          )}

          {menuItems.map((item, index) => {
            const showItem = item.requiresLogin ? (auth.data ? true : false) : true;
            if (!showItem) return null;

            const active = isActive(item.route);

            const getIcon = () => {
              switch (item.icon) {
                case 'book':
                  return (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                      />
                    </svg>
                  );
                case 'video':
                  return (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
                      />
                    </svg>
                  );
                default:
                  return <Icon name={item.icon || 'circle'} size="md" />;
              }
            };

            return (
              <Link
                key={index}
                to={item.route}
                className={`flex items-center ${isOpen ? 'space-x-3 px-4' : 'justify-center px-2'} py-3 rounded-lg ${
                  active ? 'text-white' : 'text-gray-700 hover:bg-gray-100'
                }`}
                style={active ? { backgroundColor: activeBgColor(item.route) } : undefined}
                title={!isOpen ? item.label : ''}
              >
                {getIcon()}
                {isOpen && <span className="font-medium text-sm">{item.label}</span>}
              </Link>
            );
          })}

          {auth.data && (
            <button
              onClick={() => history.push('/chat')}
              className={`flex items-center ${isOpen ? 'space-x-3 px-4' : 'justify-center px-2'} py-3 rounded-lg w-full relative ${
                isActive('/chat') ? 'text-white' : 'text-gray-700 hover:bg-gray-100'
              }`}
              style={isActive('/chat') ? { backgroundColor: activeBgColor('/chat') } : undefined}
              title={!isOpen ? t('GENERAL.chat', 'Chat') : ''}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155"
                />
              </svg>
              {isOpen && <span className="font-medium text-sm">{t('GENERAL.chat', 'Chat')}</span>}
              {hasUnreadMessages && (
                <span
                  className={`absolute ${isOpen ? 'right-4' : 'right-2'} top-1/2 transform -translate-y-1/2 bg-red-500 rounded-full h-2 w-2`}
                ></span>
              )}
            </button>
          )}
        </nav>

        {auth.data && (
          <div className={`border-t border-gray-200 ${isOpen ? 'p-4' : 'p-2'}`}>
            <div className="relative" data-user-dropdown>
              <button
                type="button"
                className={`flex items-center ${isOpen ? 'space-x-3' : 'justify-center'} w-full ${
                  isOpen ? 'px-4 py-3' : 'p-2'
                } rounded-lg hover:bg-gray-100 transition-colors`}
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <img
                  className="w-10 h-10 rounded-full"
                  src={`https://ui-avatars.com/api/?name=${auth.data.username}&background=random`}
                  alt="user avatar"
                />
                {isOpen && (
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{auth.data.username}</p>
                    <p className="text-xs text-gray-500 truncate">{auth.data.email}</p>
                  </div>
                )}
              </button>

              {showDropdown && (
                <div
                  className={`absolute ${
                    isOpen ? 'bottom-full left-0 right-0 mb-2' : 'left-full top-0 ml-2'
                  } bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden z-50`}
                >
                  <button
                    onClick={() => {
                      history.push('/profile');
                      setShowDropdown(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    <Icon name="user" size="sm" className="mr-3" />
                    {t('PROFILE.title')}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    <Icon name="logout" size="sm" className="mr-3" />
                    {t('PROFILE.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
